import { useEffect, useState } from "react";

/**
 * V2.0.4 — Single source of truth for "is this a mobile / tablet viewport".
 *
 * Returns `{ isMobile, isTablet }` reactive booleans.
 *   - isMobile = viewport width ≤ 640 px (phone portrait + smaller).
 *   - isTablet = viewport width ≤ 1024 px (phone + iPad-class).
 *
 * Backed by `window.matchMedia` so the values update LIVE as the user
 * rotates the device / resizes the desktop browser. SSR-safe — defaults to
 * `{ false, false }` when `window` is undefined so we don't crash during
 * server render.
 */
export default function useIsMobile() {
  const initial = () => {
    if (typeof window === "undefined") return { isMobile: false, isTablet: false };
    return {
      isMobile: window.matchMedia("(max-width: 640px)").matches,
      isTablet: window.matchMedia("(max-width: 1024px)").matches,
    };
  };
  const [state, setState] = useState(initial);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mqMobile = window.matchMedia("(max-width: 640px)");
    const mqTablet = window.matchMedia("(max-width: 1024px)");
    const update = () =>
      setState({ isMobile: mqMobile.matches, isTablet: mqTablet.matches });
    // matchMedia supports both addEventListener (modern) and addListener
    // (Safari < 14); use whichever is available.
    if (mqMobile.addEventListener) {
      mqMobile.addEventListener("change", update);
      mqTablet.addEventListener("change", update);
    } else {
      mqMobile.addListener(update);
      mqTablet.addListener(update);
    }
    return () => {
      if (mqMobile.removeEventListener) {
        mqMobile.removeEventListener("change", update);
        mqTablet.removeEventListener("change", update);
      } else {
        mqMobile.removeListener(update);
        mqTablet.removeListener(update);
      }
    };
  }, []);
  return state;
}
